// Animar desde el lienzo — ADR-2026-09-16-las-herramientas-del-lienzo-abren-en-panel.
//
// El menú es EL MISMO que el del visor (`VideoMenu`, ADR-2026-09-16-los-menus-del-visor-son-componentes):
// el clip de muestra, y después la descripción sugerida editable, la duración y las
// escenas múltiples. Acá viven el estado y las reglas, las mismas del visor y del modo
// Animar: es de Pro/Ultra, cobra por segundo (el doble con multi-escena), la sugerencia
// se pide al pasar de paso, y la promesa se entrega sin esperarla.

import { useCallback, useEffect, useRef, useState } from 'react';
import { VideoMenu, type PasoVideoMenu } from '../../../components/VideoMenu';
import { VIDEO_DURATION_DEFAULT } from '../../../pages/v2/components/VideoConfigSheet';
import { isProOrUltra } from '../../../lib/planCapabilities';
import { supabase } from '../../../lib/supabase';
import {
  buildAnimarVideoBody,
  fetchSuggestedVideoPromptForSource,
  generateVideoFromImage,
} from '../../../lib/videoFromImage';
import type { PropsDeHerramienta } from './herramienta';
import { consultarSaldo, tipoDeLimite } from './saldo';

export const PanelAnimar: React.FC<PropsDeHerramienta> = ({
  celda,
  ratio,
  onCerrar,
  onGeneracion,
  onLimite,
  onError,
}) => {
  const [abierto, setAbierto] = useState(false);
  const [paso, setPaso] = useState<PasoVideoMenu>('select');
  const [prompt, setPrompt] = useState('');
  const [cargandoPrompt, setCargandoPrompt] = useState(false);
  const [duracion, setDuracion] = useState(VIDEO_DURATION_DEFAULT);
  const [multiEscena, setMultiEscena] = useState(false);
  const promptPedidoRef = useRef(false);
  const generandoRef = useRef(false);
  // Paridad de cobro con el backend: multi-escena cobra segundos × 2.
  const costo = multiEscena ? duracion * 2 : duracion;

  // Guard de plan ANTES de mostrar el menú, como el visor: Animar es de Pro/Ultra.
  useEffect(() => {
    let vivo = true;
    void consultarSaldo(celda.companyId).then((s) => {
      if (!vivo) return;
      if (s.plan && !isProOrUltra(s.plan)) {
        onError('Animar imágenes está disponible para los planes Pro y Ultra.');
        onCerrar();
        return;
      }
      setAbierto(true);
    });
    return () => {
      vivo = false;
    };
  }, [celda.companyId, onCerrar, onError]);

  // "Siguiente": al segundo paso, y la sugerencia una sola vez.
  const siguiente = useCallback(() => {
    setPaso('prompt');
    if (promptPedidoRef.current) return;
    promptPedidoRef.current = true;
    setCargandoPrompt(true);
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      return fetchSuggestedVideoPromptForSource({ tipo: 'generada', imageId: celda.imageId }, user.id);
    })()
      .then(setPrompt)
      .catch(() => setPrompt(''))
      .finally(() => setCargandoPrompt(false));
  }, [celda.imageId]);

  const generar = async () => {
    if (generandoRef.current) return;
    generandoRef.current = true;
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No pudimos identificar tu cuenta. Recargá la página.');
      const saldo = await consultarSaldo(celda.companyId);
      if (saldo.restantes !== null && saldo.restantes < costo) {
        onLimite(tipoDeLimite(saldo.plan));
        onCerrar();
        return;
      }
      const body = buildAnimarVideoBody(
        { tipo: 'generada', imageId: celda.imageId },
        { userId: user.id, prompt, duracion, multiplesEscenas: multiEscena },
      );
      onGeneracion({
        tipo: 'animar',
        total: 1,
        creditos: costo,
        ratio,
        producto: celda.titulo,
        esVideo: true,
        origenId: celda.imageId,
        resultado: generateVideoFromImage(body, { companyId: celda.companyId }).then((id) => {
          if (!id) throw new Error('No se pudo generar el video. Intentá nuevamente.');
          return [id];
        }),
      });
      onCerrar();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'No se pudo generar el video. Intentá nuevamente.');
    } finally {
      generandoRef.current = false;
    }
  };

  return (
    <VideoMenu
      open={abierto}
      step={paso}
      onStepChange={setPaso}
      prompt={prompt}
      onPromptChange={setPrompt}
      loadingPrompt={cargandoPrompt}
      duration={duracion}
      onDurationChange={setDuracion}
      multiScene={multiEscena}
      onMultiSceneChange={setMultiEscena}
      cost={costo}
      onClose={onCerrar}
      onNext={siguiente}
      onGenerate={() => void generar()}
    />
  );
};
